import { Injectable } from '@angular/core';

export type SupportRequestCategory = 'visibility' | 'capacity' | 'website' | 'platform';
export type WordpressNeed = 'new' | 'fix';

export function isWebsiteOrPlatformCategory(
  c: SupportRequestCategory | string | null | undefined
): boolean {
  return c === 'website' || c === 'platform';
}

export type SupportRequestVisibilityKind =
  | 'activity'
  | 'social'
  | 'op-ed'
  | 'webinar'
  | 'wordpress';

/** Returns `true` if `val` is a valid {@link SupportRequestCategory}. */
export function isSupportRequestCategory(val: string | null): val is SupportRequestCategory {
  return val === 'visibility' || val === 'capacity' || val === 'website' || val === 'platform';
}

/** Returns `true` if `val` is a valid {@link SupportRequestVisibilityKind}. */
export function isSupportRequestVisibilityKind(val: string | null): val is SupportRequestVisibilityKind {
  return val === 'activity' || val === 'social' || val === 'op-ed' || val === 'webinar' || val === 'wordpress';
}

export type SupportRequestWebsitePlatformKind =
  | 'change-org-name'
  | 'general-question'
  | 'help-completing-survey'
  | 'help-survey-errors'
  | 'request-call'
  | 'suggestion';

/** Returns `true` if `val` is a valid {@link SupportRequestWebsitePlatformKind}. */
export function isSupportRequestWebsitePlatformKind(
  val: string | null
): val is SupportRequestWebsitePlatformKind {
  return (
    val === 'change-org-name' ||
    val === 'general-question' ||
    val === 'help-completing-survey' ||
    val === 'help-survey-errors' ||
    val === 'request-call' ||
    val === 'suggestion'
  );
}

export type SupportRequestCapacityKind =
  | 'mentoring'
  | 'diaspora'
  | 'wordpress'
  | 'funding';

/** Returns `true` if `val` is a valid {@link SupportRequestCapacityKind}. */
export function isSupportRequestCapacityKind(val: string | null): val is SupportRequestCapacityKind {
  return val === 'mentoring' || val === 'diaspora' || val === 'wordpress' || val === 'funding';
}

/** Returns `true` if `val` is a valid {@link WordpressNeed}. */
export function isWordpressNeed(val: string | null): val is WordpressNeed {
  return val === 'new' || val === 'fix';
}

const SESSION_CAT = 'sr.nss.supportRequest.category';
const SESSION_KIND = 'sr.nss.supportRequest.visibilityKind';
const SESSION_WEBSITE_KIND = 'sr.nss.supportRequest.websiteKind';
const SESSION_CAPACITY_KIND = 'sr.nss.supportRequest.capacityKind';
const SESSION_WP_NEED = 'sr.nss.supportRequest.wpNeed';
const SESSION_WP_DOMAIN_HAS = 'sr.nss.supportRequest.wpDomainHas';
const SESSION_WP_DOMAIN_URL = 'sr.nss.supportRequest.wpDomainUrl';
const SESSION_WP_HOSTING_HAS = 'sr.nss.supportRequest.wpHostingHas';
const SESSION_WP_LOGO_HAS = 'sr.nss.supportRequest.wpLogoHas';
const SESSION_WP_STYLE_HAS = 'sr.nss.supportRequest.wpStyleHas';
const SESSION_WP_PICTURES_HAS = 'sr.nss.supportRequest.wpPicturesHas';
const SESSION_WP_GOOGLE_HAS = 'sr.nss.supportRequest.wpGoogleHas';
const SESSION_WP_ACK_DOMAIN_HOSTING = 'sr.nss.supportRequest.wpAckDomainHosting';
const SESSION_WP_ACK_ASSETS = 'sr.nss.supportRequest.wpAckAssets';

@Injectable({ providedIn: 'root' })
export class SupportRequestWizardService {
  category: SupportRequestCategory | null = null;
  visibilityKind: SupportRequestVisibilityKind | null = null;
  websitePlatformKind: SupportRequestWebsitePlatformKind | null = null;
  capacityKind: SupportRequestCapacityKind | null = null;

  wordpressNeed: WordpressNeed | null = null;
  domainHas: boolean | null = null;
  domainUrl = '';
  hostingHas: boolean | null = null;
  logoHas: boolean | null = null;
  styleGuideHas: boolean | null = null;
  picturesHas: boolean | null = null;
  googleSearchHas: boolean | null = null;
  ackDomainHosting: boolean | null = null;
  ackAssets: boolean | null = null;

  constructor() {
    this.hydrateFromSession();
  }

  reset(): void {
    this.category = null;
    this.visibilityKind = null;
    this.websitePlatformKind = null;
    this.capacityKind = null;
    this.wordpressNeed = null;
    this.domainHas = null;
    this.domainUrl = '';
    this.hostingHas = null;
    this.logoHas = null;
    this.styleGuideHas = null;
    this.picturesHas = null;
    this.googleSearchHas = null;
    this.ackDomainHosting = null;
    this.ackAssets = null;
    this.persistSession();
  }

  /** Persists to `sessionStorage` under `SESSION_CAT`. Only known categories are stored. */
  setCategory(category: string): void {
    if (isSupportRequestCategory(category)) {
      this.category = category;
      this.persistSession();
    }
  }

  /**
   * Persists the step-2 kind selection to `sessionStorage`.
   *
   * @description The `wordpress` kind is valid for both visibility and capacity,
   * so the active category is consulted first to route the value into the
   * correct slot. Other kinds are unique to a single category and dispatched by
   * type-guard match. Sibling kind fields are cleared to prevent stale
   * cross-flow state. Unrecognized values are silently ignored.
   * @param {string} k - Raw kind string from a tile selection or route param.
   * @returns {void}
   */
  setKind(k: string): void {
    if (this.category === 'capacity' && isSupportRequestCapacityKind(k)) {
      this.capacityKind = k;
      this.visibilityKind = null;
      this.websitePlatformKind = null;
    } else if (isSupportRequestVisibilityKind(k)) {
      this.visibilityKind = k;
      this.websitePlatformKind = null;
      this.capacityKind = null;
    } else if (isSupportRequestWebsitePlatformKind(k)) {
      this.websitePlatformKind = k;
      this.visibilityKind = null;
      this.capacityKind = null;
    } else {
      return;
    }

    this.persistSession();
  }

  setWordpressNeed(need: WordpressNeed): void { this.wordpressNeed = need; this.persistSession(); }
  setDomainHas(val: boolean): void { this.domainHas = val; this.persistSession(); }
  setDomainUrl(url: string): void { this.domainUrl = url; this.persistSession(); }
  setHostingHas(val: boolean): void { this.hostingHas = val; this.persistSession(); }
  setLogoHas(val: boolean): void { this.logoHas = val; this.persistSession(); }
  setStyleGuideHas(val: boolean): void { this.styleGuideHas = val; this.persistSession(); }
  setPicturesHas(val: boolean): void { this.picturesHas = val; this.persistSession(); }
  setGoogleSearchHas(val: boolean): void { this.googleSearchHas = val; this.persistSession(); }
  setAckDomainHosting(val: boolean): void { this.ackDomainHosting = val; this.persistSession(); }
  setAckAssets(val: boolean): void { this.ackAssets = val; this.persistSession(); }

  private hydrateFromSession(): void {
    try {
      const c = sessionStorage.getItem(SESSION_CAT);
      if (isSupportRequestCategory(c)) this.category = c;

      const k = sessionStorage.getItem(SESSION_KIND);
      if (isSupportRequestVisibilityKind(k)) this.visibilityKind = k;

      const wk = sessionStorage.getItem(SESSION_WEBSITE_KIND);
      if (isSupportRequestWebsitePlatformKind(wk)) this.websitePlatformKind = wk;

      const ck = sessionStorage.getItem(SESSION_CAPACITY_KIND);
      if (isSupportRequestCapacityKind(ck)) this.capacityKind = ck;

      const wpNeed = sessionStorage.getItem(SESSION_WP_NEED);
      if (isWordpressNeed(wpNeed)) this.wordpressNeed = wpNeed;

      this.domainHas = this.hydrateBoolean(SESSION_WP_DOMAIN_HAS);
      this.domainUrl = sessionStorage.getItem(SESSION_WP_DOMAIN_URL) ?? '';
      this.hostingHas = this.hydrateBoolean(SESSION_WP_HOSTING_HAS);
      this.logoHas = this.hydrateBoolean(SESSION_WP_LOGO_HAS);
      this.styleGuideHas = this.hydrateBoolean(SESSION_WP_STYLE_HAS);
      this.picturesHas = this.hydrateBoolean(SESSION_WP_PICTURES_HAS);
      this.googleSearchHas = this.hydrateBoolean(SESSION_WP_GOOGLE_HAS);
      this.ackDomainHosting = this.hydrateBoolean(SESSION_WP_ACK_DOMAIN_HOSTING);
      this.ackAssets = this.hydrateBoolean(SESSION_WP_ACK_ASSETS);
    } catch {
      /* private mode / SSR */
    }
  }

  private hydrateBoolean(key: string): boolean | null {
    const v = sessionStorage.getItem(key);
    if (v === 'true') return true;
    if (v === 'false') return false;
    return null;
  }

  private persistSession(): void {
    try {
      if (this.category) sessionStorage.setItem(SESSION_CAT, this.category);
      else sessionStorage.removeItem(SESSION_CAT);

      if (this.visibilityKind) sessionStorage.setItem(SESSION_KIND, this.visibilityKind);
      else sessionStorage.removeItem(SESSION_KIND);

      if (this.websitePlatformKind) sessionStorage.setItem(SESSION_WEBSITE_KIND, this.websitePlatformKind);
      else sessionStorage.removeItem(SESSION_WEBSITE_KIND);

      if (this.capacityKind) sessionStorage.setItem(SESSION_CAPACITY_KIND, this.capacityKind);
      else sessionStorage.removeItem(SESSION_CAPACITY_KIND);

      if (this.wordpressNeed) sessionStorage.setItem(SESSION_WP_NEED, this.wordpressNeed);
      else sessionStorage.removeItem(SESSION_WP_NEED);

      this.persistBoolean(SESSION_WP_DOMAIN_HAS, this.domainHas);
      if (this.domainUrl) sessionStorage.setItem(SESSION_WP_DOMAIN_URL, this.domainUrl);
      else sessionStorage.removeItem(SESSION_WP_DOMAIN_URL);
      this.persistBoolean(SESSION_WP_HOSTING_HAS, this.hostingHas);
      this.persistBoolean(SESSION_WP_LOGO_HAS, this.logoHas);
      this.persistBoolean(SESSION_WP_STYLE_HAS, this.styleGuideHas);
      this.persistBoolean(SESSION_WP_PICTURES_HAS, this.picturesHas);
      this.persistBoolean(SESSION_WP_GOOGLE_HAS, this.googleSearchHas);
      this.persistBoolean(SESSION_WP_ACK_DOMAIN_HOSTING, this.ackDomainHosting);
      this.persistBoolean(SESSION_WP_ACK_ASSETS, this.ackAssets);
    } catch {
      /* ignore */
    }
  }

  private persistBoolean(key: string, val: boolean | null): void {
    if (val !== null) sessionStorage.setItem(key, String(val));
    else sessionStorage.removeItem(key);
  }
}
